import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import tripPlannerConfig from '#/trip-planner-config';
import { ROUTE_COLOR_QUERY } from '#/trip-planner/graphql/queries';
import type { Route } from '#/types/otp2types.generated';
import type { ContrastLookup } from '#/types/contrast';
import { CACHE_DURATION, NODE_ENVS } from '#/shared/constants';
import {
  calculateContrastColors,
  normalizeHex,
} from '#/shared/utils/colorContrast';

const { api } = tripPlannerConfig;

// GraphQL response types
type RouteColor = Pick<Route, 'gtfsId' | 'color'>;

interface GraphQLResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

interface RouteColorsData {
  routes: RouteColor[];
}

export interface RouteColorsResponse {
  contrastLookup: ContrastLookup;
}

export const graphqlApi = createApi({
  reducerPath: 'graphqlApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${api.host}${api.paths.plan}`,
    prepareHeaders: headers => {
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: build => ({
    getRouteColors: build.query<RouteColorsResponse, undefined>({
      query: () => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: ROUTE_COLOR_QUERY,
        }),
      }),
      transformResponse: (response: GraphQLResponse<RouteColorsData>) => {
        const routes = response.data?.routes ?? [];

        // Normalize colors and ensure they have a leading '#'
        const uniqueColors = new Set<string>(
          routes
            .map(route => normalizeHex(route.color))
            .filter((colorHex): colorHex is string => Boolean(colorHex))
        );

        // Calculate contrast adjustments for each unique color
        const contrastLookup: ContrastLookup = {};

        uniqueColors.forEach(colorHex => {
          try {
            contrastLookup[colorHex] = calculateContrastColors(colorHex);
          } catch (e) {
            if (process.env.NODE_ENV !== NODE_ENVS.PROD) {
              console.warn('calculateContrastColors failed for', colorHex, e);
            }
            contrastLookup[colorHex] = { light: null, dark: null };
          }
        });

        // Include colors where either light OR dark is populated
        const filteredContrastLookup: ContrastLookup = Object.fromEntries(
          Object.entries(contrastLookup).filter(
            ([, v]) => v.light !== null || v.dark !== null
          )
        );

        return {
          contrastLookup: filteredContrastLookup,
        };
      },
      keepUnusedDataFor: CACHE_DURATION.ONE_HOUR,
    }),
  }),
});

export const { useGetRouteColorsQuery } = graphqlApi;
